import { PageControls, usePagination } from '@/components/shared/pagination/Pagination';
import tableStrings from '@/components/shared/table/table.translations';
import { getAllowancePeriodLabel, LeaveType } from '@/services/leaveManagement/leave.types';
import { FC, FunctionComponent } from 'react';
import { FiCheck, FiEdit } from 'react-icons/fi';
import strings from '../admin.translations';

const columnWidths = {
  name: '42%',
  balance: '18%',
  color: '14%',
  managerOnly: '14%',
  edit: '12%'
};

const pageSize = 20;

interface LeaveTypeTableHeaderProps {
  isAdvanced: boolean;
}

const LeaveTypeTableHeader: FC<LeaveTypeTableHeaderProps> = ({ isAdvanced }) => (
  <thead>
    <tr>
      <th
        className="px-6 pb-2 pt-4 text-left text-xs font-medium tracking-wider text-secondary-std"
        style={{ width: columnWidths.name }}
      >
        {strings.leaveTypeSection.table.name}
      </th>
      <th
        className="px-6 pb-2 pt-4 text-center text-xs font-medium tracking-wider text-secondary-std"
        style={{ width: columnWidths.balance }}
      >
        {strings.leaveTypeSection.table.balance}
      </th>
      <th
        className="px-6 pb-2 pt-4 text-center text-xs font-medium tracking-wider text-secondary-std"
        style={{ width: columnWidths.color }}
      >
        {strings.leaveTypeSection.table.color}
      </th>
      <th
        className="px-6 pb-2 pt-4 text-center text-xs font-medium tracking-wider text-secondary-std"
        style={{ width: columnWidths.managerOnly }}
      >
        {strings.leaveTypeSection.table.managerOnly}
      </th>
      {isAdvanced && (
        <th
          className="px-6 pb-2 pt-4 text-center text-xs font-medium tracking-wider text-secondary-std"
          style={{ width: columnWidths.edit }}
        >
          {strings.tasksSection.table.edit}
        </th>
      )}
    </tr>
  </thead>
);

interface LeaveTypeTableRowProps {
  leaveType: LeaveType;
  index: number;
  onEdit: (skillId: string) => void;
  isAdvanced: boolean;
}

const AdminLeaveTypeTableRow: FC<LeaveTypeTableRowProps> = ({ leaveType, index, onEdit, isAdvanced }) => {
  const alternateRowStyling = index % 2 === 0 ? 'bg-secondary-std' : 'bg-primary-std';
  const disabledRowStyling = 'bg-gray-300';

  return (
    <tr className={leaveType.active ? alternateRowStyling : disabledRowStyling}>
      <td className="px-4 py-4 text-sm text-primary-std sm:px-6" style={{ width: columnWidths.name }}>
        <div className={`flex h-[2.2rem] flex-col justify-center`}>
          <div>
            <p className="font-semibold">{leaveType.name}</p>
            <p>{leaveType.properties.displayName}</p>
            {!leaveType.active && <p>{strings.leaveTypeSection.table.disabled}</p>}
          </div>
        </div>
      </td>
      <td className="px-4 py-4 text-sm text-primary-std sm:px-6" style={{ width: columnWidths.balance }}>
        <div className="flex justify-center">
          <p>
            {leaveType.allowanceAmount === 0 ? (
              '-'
            ) : (
              <>
                <span>{leaveType.allowanceAmount}</span>{' '}
                {getAllowancePeriodLabel(leaveType.allowancePeriod)}
              </>
            )}
          </p>
        </div>
      </td>
      <td className="px-4 py-4 text-sm text-primary-std sm:px-6" style={{ width: columnWidths.color }}>
        <div className="flex justify-center">
          <span
            className="h-6 w-6 rounded-full border border-primary-std"
            style={{ backgroundColor: leaveType.properties.color }}
          />
        </div>
      </td>
      <td className="px-4 py-4 text-sm text-primary-std sm:px-6" style={{ width: columnWidths.managerOnly }}>
        <div className="flex justify-center">
          {leaveType.properties.managerOnly ? <FiCheck className="h-5 w-5 text-green-600" /> : null}
        </div>
      </td>
      {isAdvanced && (
        <td className="px-4 py-4 text-sm text-primary-std sm:px-6" style={{ width: columnWidths.edit }}>
          <div className="flex justify-center">
            <button
              type="button"
              aria-label={`${strings.tasksSection.table.edit} ${leaveType.properties.displayName}`}
              className="text-secondary-std hover:text-primary-std"
              onClick={() => onEdit(leaveType.id)}
            >
              <FiEdit className="h-5 w-5" />
            </button>
          </div>
        </td>
      )}
    </tr>
  );
};

type AdminLeaveTypeTableProps = {
  leaveTypes: LeaveType[];
  isAdvanced: boolean;
  onEdit: (leaveTypeId: string) => void;
};

const AdminLeaveTypeTable: FunctionComponent<AdminLeaveTypeTableProps> = ({ leaveTypes, isAdvanced, onEdit }) => {
  const [offset, limit, controls] = usePagination(pageSize, leaveTypes.length);
  const paginatedLeaveTypes = leaveTypes.slice(offset, offset + limit);

  return (
    <div className="w-full overflow-hidden">
      <table className="min-w-full table-fixed divide-y divide-primary-std border border-primary-std">
        <LeaveTypeTableHeader isAdvanced={isAdvanced} />
        <tbody className="divide-y divide-primary-std">
          {paginatedLeaveTypes.map((l, index) => {
            return (
              <AdminLeaveTypeTableRow key={l.id} leaveType={l} index={index} onEdit={onEdit} isAdvanced={isAdvanced} />
            );
          })}
        </tbody>
      </table>
      {leaveTypes.length > 0 ? (
        <PageControls {...controls} />
      ) : (
        <div className="flex w-full justify-center border-t border-primary-std px-6 py-5">
          <p className="py-1 text-xs font-medium uppercase text-primary-std">{tableStrings.nothingToShow}</p>
        </div>
      )}
    </div>
  );
};

export default AdminLeaveTypeTable;
